import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconTool,
  IconEye,
  IconLoader2,
} from '@tabler/icons-react';
import type { Node, Edge } from 'reactflow';
import type { Survey } from './types';
import { INITIAL_NODES, INITIAL_EDGES } from './constants';
import {
  ensureStartEndNodes,
  convertFlowToSurveyFormat,
  generateFlowFromTemplate,
  buildSurveyLink,
  isConditionalEdge,
} from './utils';
import { SurveyFormFields } from './components/SurveyFormFields';
import { CardSurveyBuilder } from './components/CardSurveyBuilder/CardSurveyBuilder';
import { SurveyPreview } from './components/SurveyPreview';
import { flowToCard, cardToFlow } from './utils/cardConverter';
import { isCardSurvey, isFlowSurvey, type CardSurvey, DONE_STEP_JSON_ID, DONE_QUESTION_ID } from './types';
import { isNewFormat, isOldFlowFormat } from './utils/surveyFormatDetection';
import {
  buildOldSummaryFromFlow,
  buildNewSummaryFromCard,
  compareOldNewSummaries,
  type MigrationComparisonResult,
} from './utils/surveyMigrationCompare';
import { MigrationComparisonView } from './components/MigrationComparisonView';
import { SurveyValidationAlert } from './components/CardSurveyBuilder/SurveyValidationAlert';
import { validateSurvey, getErrors } from './utils/surveyValidation';
import { normalizeTriggerValues } from './utils/pathTriggerValues';
import api from '../../lib/api';

/** When editing, if payload is old format we show comparison before setting card survey */
interface MigrationState {
  oldPayload: unknown;
  convertedCardSurvey: CardSurvey;
  comparison: MigrationComparisonResult;
}

/** Minimal shape from GET /surveys when extracting brand options */
interface SurveyListItemForBrands {
  brand_id?: string | null;
}

/** Payload sent to POST /surveys or PUT /surveys/:id */
interface SurveySavePayload {
  name: string;
  description: string;
  link?: string;
  start_date?: string;
  end_date?: string;
  data_reward?: string;
  brand_id?: string;
  icon_url?: string;
  is_active: boolean;
  questions?: CardSurvey & {
    flowFormat?: { nodes: unknown[]; edges: unknown[]; survey: unknown[] };
  };
}

import { toast } from 'sonner';

/** Format a Date or ISO string as YYYY-MM-DD in local time (for date inputs). */
function toDateInputValue(dateInput: string | Date | null | undefined): string {
  if (dateInput == null || dateInput === '') return '';
  const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function countRealQuestions(survey: CardSurvey | null): number {
  if (!survey?.questions?.length) return 0;
  return survey.questions.filter(
    (q) => q.type !== 'done' && q.id !== DONE_STEP_JSON_ID && q.id !== DONE_QUESTION_ID,
  ).length;
}

/** Legacy (old) question meta shape used when loading old-format flow surveys */
interface LegacyNodeMeta {
  type?: string;
  conditional_config?: { condition?: string; number?: number; response_question?: string; type?: string };
  condition?: string;
}

/** Legacy node data shape (old format with meta) */
interface LegacyNodeData {
  meta?: LegacyNodeMeta;
  label?: string;
  conditionalBranchId?: string;
  parentConditionalOption?: string;
  parentConditionalSourceId?: string;
  autoGeneratedConditional?: boolean;
  createConditionalQuestions?: boolean;
  calculatedHeight?: number;
  [key: string]: unknown;
}

/** Legacy node shape accepted by normalizeNodeToNewFormat */
interface LegacyNode {
  id: string;
  position?: { x: number; y: number };
  positionAbsolute?: { x: number; y: number };
  style?: Record<string, unknown>;
  data?: LegacyNodeData;
  type?: string;
  [key: string]: unknown;
}

/** Legacy edge shape accepted by normalizeEdgeToNewFormat */
interface LegacyEdge {
  id: string;
  source: string;
  target: string;
  data?: Record<string, unknown>;
  label?: string | null;
  style?: Record<string, unknown>;
  [key: string]: unknown;
}

/** Legacy questions payload that has a .survey array (old flow format) */
interface LegacyQuestionsWithSurvey {
  survey?: unknown[];
}

/** Normalize GET /surveys response to an array (handles raw array or wrapped { data: [] } from API). */
function getSurveysListFromResponse(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as { data: unknown[] }).data)) {
    return (data as { data: unknown[] }).data;
  }
  return [];
}

/**
 * Normalizes nodes to ensure they're in the new format (removes old format properties)
 * This ensures that when surveys are saved and reloaded, they maintain the new format
 */
const normalizeNodeToNewFormat = (node: LegacyNode): Node => {
  if (node.id === 'start' || node.id === 'end') {
    return {
      ...node,
      positionAbsolute: node.positionAbsolute || node.position,
      style: {
        ...node.style,
        cursor: node.style?.cursor === 'grab' ? 'pointer' : (node.style?.cursor || 'pointer'),
      },
    } as Node;
  }

  const hasMeta = node.data?.meta && typeof node.data.meta === 'object';
  
  let normalizedData: Record<string, unknown> = { ...node.data };
  
  if (hasMeta) {
    const meta = node.data!.meta as LegacyNodeMeta;
    const oldData = node.data ?? {};
    
    const questionType = meta.type === 'radio' ? 'options' : 
                        meta.type === 'checkbox' ? 'choices' : 
                        undefined;
    
    normalizedData = {
      label: oldData.label || node.data?.label || '',
      questionType,
      ...(node.data?.conditionalBranchId && { conditionalBranchId: node.data.conditionalBranchId }),
      ...(node.data?.parentConditionalOption && { parentConditionalOption: node.data.parentConditionalOption }),
      ...(node.data?.autoGeneratedConditional && { autoGeneratedConditional: node.data.autoGeneratedConditional }),
      ...(node.data?.parentConditionalSourceId && { parentConditionalSourceId: node.data.parentConditionalSourceId }),
      ...(node.data?.createConditionalQuestions !== undefined && { createConditionalQuestions: node.data.createConditionalQuestions }),
    };
    
    delete normalizedData.meta;
    delete normalizedData.calculatedHeight;
    
    if (node.id.includes('.') && meta.conditional_config) {
      if (!normalizedData.parentConditionalOption && meta.condition) {
        normalizedData.parentConditionalOption = meta.condition;
      }
      if (!normalizedData.parentConditionalSourceId) {
        const nodeIdParts = node.id.split('.');
        normalizedData.parentConditionalSourceId = nodeIdParts[0];
      }
    }
    
    if (meta.conditional_config && !node.id.includes('.')) {
      normalizedData.createConditionalQuestions = true;
    }
  } else {
    delete normalizedData.meta;
    delete normalizedData.calculatedHeight;
  }
  
  const normalizedStyle: Record<string, unknown> = {
    ...node.style,
    cursor: node.style?.cursor === 'grab' ? 'pointer' : (node.style?.cursor || 'pointer'),
  };
  delete normalizedStyle.minHeight;
  if (node.id === 'start' && !normalizedStyle.border) {
    normalizedStyle.border = '1px solid #1976d2';
    normalizedStyle.borderColor = '#1976d2';
  }
  if (node.id === 'end' && !normalizedStyle.border) {
    normalizedStyle.border = '1px solid #2e7d32';
    normalizedStyle.borderColor = '#2e7d32';
  }

  const normalizedNode: Node = {
    ...node,
    positionAbsolute: node.positionAbsolute || node.position,
    data: normalizedData,
    style: normalizedStyle,
  } as Node;

  const out = normalizedNode as Record<string, unknown>;
  if ('dragging' in out) out.dragging = false;
  if ('selected' in out) out.selected = false;

  return normalizedNode;
};

/**
 * Normalizes edges to ensure they're in the new format
 */
const normalizeEdgeToNewFormat = (edge: LegacyEdge, allNodes: Node[]): Edge => {
  const normalizedEdge: Record<string, unknown> = {
    ...edge,
    reconnectable: true,
  };
  
  if ('selected' in normalizedEdge) {
    delete normalizedEdge.selected;
  }
  
  if (isConditionalEdge(edge as Edge)) {
    const conditionalOption = (edge.data?.conditionalOption as string) ??
                             (typeof edge.label === 'string' ? edge.label : null);

    if (conditionalOption) {
      const data = (normalizedEdge.data ?? {}) as Record<string, unknown>;
      data.conditionalOption = conditionalOption;
      data.isConditional = true;

      if (!data.conditionalBranchId && edge.target) {
        const targetNode = allNodes.find((n) => n.id === edge.target);
        if (targetNode?.data?.conditionalBranchId) {
          data.conditionalBranchId = targetNode.data.conditionalBranchId;
        }
      }
      if (!data.linkedFromOption) {
        data.linkedFromOption = { sourceId: edge.source, option: conditionalOption };
      }
      normalizedEdge.data = data;

      const style = (normalizedEdge.style ?? {}) as Record<string, unknown>;
      style.stroke = style.stroke ?? '#d32f2f';
      style.strokeDasharray = style.strokeDasharray ?? '6 4';
      normalizedEdge.style = style;
      normalizedEdge.animated = true;
    }
  }

  if (!isConditionalEdge(edge as Edge)) {
    const data = (normalizedEdge.data ?? {}) as Record<string, unknown>;
    data.linear = true;
    normalizedEdge.data = data;
  }

  return normalizedEdge as Edge;
};

export function CreateEditSurveyPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [surveyName, setSurveyName] = useState('');
  const [surveyDescription, setSurveyDescription] = useState('');
  const [surveyLink, setSurveyLink] = useState('');
  const [surveyStartDate, setSurveyStartDate] = useState('');
  const [surveyEndDate, setSurveyEndDate] = useState('');
  const [surveyDataReward, setSurveyDataReward] = useState('0');
  const [surveyBrandId, setSurveyBrandId] = useState('megsApp');
  const [surveyIconUrl, setSurveyIconUrl] = useState('https://storage.googleapis.com/megsapp_demo/megsApp.jpeg');
  const [surveyIsActive, setSurveyIsActive] = useState(true);
  const [expandedAccordion, setExpandedAccordion] = useState<string[]>(['panel1']);
  const [validationErrors, setValidationErrors] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [brandOptionLabels, setBrandOptionLabels] = useState<Record<string, string>>({});
  const [cardSurvey, setCardSurvey] = useState<CardSurvey | null>(null);
  const cardSurveyRef = useRef<CardSurvey | null>(null);
  const [migrationState, setMigrationState] = useState<MigrationState | null>(null);

  const handleCardSurveyChange = useCallback((survey: CardSurvey) => {
    cardSurveyRef.current = survey;
    setCardSurvey(survey);
  }, []);

  const removeDuplicateEdges = useCallback((currentEdges: Edge[]): Edge[] => {
    const seenIds = new Set<string>();
    const uniqueEdges: Edge[] = [];
    let duplicateCount = 0;
    
    for (const edge of currentEdges) {
      if (!seenIds.has(edge.id)) {
        seenIds.add(edge.id);
        uniqueEdges.push(edge);
      } else {
        duplicateCount++;
        console.log('🧹 Removing duplicate edge:', {
          edgeId: edge.id,
          source: edge.source,
          target: edge.target,
        });
      }
    }
    
    if (duplicateCount > 0) {
      console.log(`🧹 Cleaned up ${duplicateCount} duplicate edge(s)`);
    }
    
    return uniqueEdges;
  }, []);

  const isDateRangeValid = useMemo(() => {
    if (!surveyStartDate || !surveyEndDate) return true;
    const start = new Date(surveyStartDate);
    const end = new Date(surveyEndDate);
    return !isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start;
  }, [surveyStartDate, surveyEndDate]);
  
  const isBrandIdValid = useMemo(() => {
    return surveyBrandId.trim() !== '';
  }, [surveyBrandId]);
  
  const hasAtLeastOneQuestion = useMemo(() => {
    return countRealQuestions(cardSurvey) > 0;
  }, [cardSurvey]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await api.get<{ data: Array<{ brand: string; brand_id: string }> }>('/surveys/brands');
        const data = res.data?.data ?? [];
        if (Array.isArray(data) && data.length > 0) {
          const ids = data.map((x) => x.brand_id).filter(Boolean);
          setBrandOptions(ids);
          setBrandOptionLabels(Object.fromEntries(data.map((x) => [x.brand_id, x.brand ?? x.brand_id])));
          if (!isEditing && ids.length > 0) {
            const megsAppId = ids.find((id) => id.toLowerCase() === 'megsapp');
            setSurveyBrandId(megsAppId ?? ids[0]);
          }
          return;
        }
        const surveysResponse = await api.get('/surveys');
        const surveys = getSurveysListFromResponse(surveysResponse?.data) as SurveyListItemForBrands[];
        const brandSet = new Set<string>();
        surveys.forEach((survey: SurveyListItemForBrands) => {
          if (survey.brand_id != null && survey.brand_id !== '') {
            const value = String(survey.brand_id).trim();
            if (value) brandSet.add(value);
          }
        });
        const brandIds = Array.from(brandSet).sort();
        setBrandOptions(brandIds);
        if (!isEditing && brandIds.length > 0) {
          const megsAppId = brandIds.find((id) => id.toLowerCase() === 'megsapp');
          setSurveyBrandId(megsAppId ?? brandIds[0]);
        }
      } catch (err) {
        console.error('Failed to fetch brands:', err);
      }
    };
    fetchBrands();
  }, [isEditing]);

  // Keep brand_id state in sync with loaded survey / available options (Select UI can show a value while state is "").
  useEffect(() => {
    if (brandOptions.length === 0) return;
    setSurveyBrandId((current) => {
      if (current && brandOptions.includes(current)) return current;
      const loaded =
        editingSurvey?.brand_id != null && String(editingSurvey.brand_id).trim() !== ''
          ? String(editingSurvey.brand_id)
          : '';
      if (loaded && brandOptions.includes(loaded)) return loaded;
      return brandOptions[0];
    });
  }, [brandOptions, editingSurvey?.brand_id]);
  
  useEffect(() => {
    if (!isEditing || !id) return;
    
    const fetchSurvey = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/surveys/${id}`);
        const survey = response.data;
        
        const rawBrand = survey.brand_id !== undefined ? survey.brand_id : survey.brandId;
        const normalizedBrand = rawBrand === null || rawBrand === undefined || rawBrand === '' ? null : rawBrand;
        const rawReward = survey.data_reward ?? survey.dataReward;
        const dataRewardForLink =
          rawReward != null && String(rawReward).trim() !== '' ? String(rawReward) : '0';

        const normalizedSurvey: Survey = {
          ...survey,
          is_active: typeof survey.is_active === 'number' ? Boolean(survey.is_active) : survey.is_active ?? true,
          brand_id: normalizedBrand,
          data_reward: dataRewardForLink,
          link: buildSurveyLink(survey.id, dataRewardForLink),
        };

        setEditingSurvey(normalizedSurvey);
        setSurveyName(normalizedSurvey.name);
        setSurveyDescription(normalizedSurvey.description || '');
        setSurveyLink(buildSurveyLink(normalizedSurvey.id, dataRewardForLink));
        setSurveyStartDate(normalizedSurvey.start_date ? toDateInputValue(normalizedSurvey.start_date) : '');
        setSurveyEndDate(normalizedSurvey.end_date ? toDateInputValue(normalizedSurvey.end_date) : '');
        const loadedReward = rawReward != null ? String(rawReward) : '';
        const trimmed = loadedReward.trim();
        const rewardNum = trimmed !== '' ? parseFloat(trimmed) : NaN;
        const displayReward =
          trimmed === '' ? '0' : !Number.isNaN(rewardNum) && rewardNum < 0 ? '0' : loadedReward;
        setSurveyDataReward(displayReward);
        setSurveyBrandId(normalizedSurvey.brand_id !== null && normalizedSurvey.brand_id !== undefined ? String(normalizedSurvey.brand_id) : '');
        setSurveyIconUrl(normalizedSurvey.icon_url ?? 'https://storage.googleapis.com/megsapp_demo/megsApp.jpeg');
        setSurveyIsActive(normalizedSurvey.is_active !== null && normalizedSurvey.is_active !== undefined ? Boolean(Number(normalizedSurvey.is_active)) : true);
        
        let loadedCardSurvey: CardSurvey | null = null;
        let safeNodes = INITIAL_NODES;
        let safeEdges = INITIAL_EDGES;
        
        let skipSettingCardSurvey = false;

        if (normalizedSurvey.questions) {
          if (isNewFormat(normalizedSurvey.questions) && isCardSurvey(normalizedSurvey.questions)) {
            loadedCardSurvey = normalizedSurvey.questions;
            const flowData = cardToFlow(loadedCardSurvey);
            safeNodes = flowData.nodes;
            safeEdges = flowData.edges;
          }
          else if (
            (normalizedSurvey.questions as LegacyQuestionsWithSurvey).survey &&
            Array.isArray((normalizedSurvey.questions as LegacyQuestionsWithSurvey).survey) &&
            (normalizedSurvey.questions as LegacyQuestionsWithSurvey).survey!.length > 0
          ) {
            const legacyQuestions = normalizedSurvey.questions as LegacyQuestionsWithSurvey;
            const flowData = generateFlowFromTemplate(
              legacyQuestions.survey! as Parameters<typeof generateFlowFromTemplate>[0],
            );
            const nodesWithMeta = ensureStartEndNodes(flowData.nodes);
            const edgesForCard = removeDuplicateEdges(flowData.edges);
            loadedCardSurvey = flowToCard(nodesWithMeta, edgesForCard);
            const oldSummary = buildOldSummaryFromFlow(nodesWithMeta, edgesForCard);
            const newSummary = buildNewSummaryFromCard(loadedCardSurvey);
            safeNodes = ensureStartEndNodes(flowData.nodes.map((node) => normalizeNodeToNewFormat(node as LegacyNode)));
            safeEdges = flowData.edges.map((edge) => normalizeEdgeToNewFormat(edge as LegacyEdge, safeNodes));
            safeEdges = removeDuplicateEdges(safeEdges);
            const comparison = compareOldNewSummaries(oldSummary, newSummary);
            const normalizedCardSurvey: CardSurvey = {
              ...loadedCardSurvey,
              paths: (loadedCardSurvey.paths || []).map((p) => ({
                ...p,
                trigger: {
                  ...p.trigger,
                  values: normalizeTriggerValues(p.trigger?.values),
                },
              })),
            };
            setMigrationState({
              oldPayload: normalizedSurvey.questions,
              convertedCardSurvey: normalizedCardSurvey,
              comparison,
            });
            skipSettingCardSurvey = true;
          }
          else if (isOldFlowFormat(normalizedSurvey.questions) && isFlowSurvey(normalizedSurvey.questions)) {
            const legacyNodes = (normalizedSurvey.questions.nodes as LegacyNode[]).map((node: LegacyNode) => {
              if (node.type === 'default' || (!node.type && node.id !== 'start' && node.id !== 'end')) {
                return { ...node, type: 'question' };
              }
              return node;
            });
            const rawNodes = ensureStartEndNodes(legacyNodes as Node[]);
            const rawEdges =
              normalizedSurvey.questions.edges && Array.isArray(normalizedSurvey.questions.edges)
                ? removeDuplicateEdges(normalizedSurvey.questions.edges)
                : [];
            loadedCardSurvey = flowToCard(rawNodes, rawEdges);
            const oldSummary = buildOldSummaryFromFlow(rawNodes, rawEdges);
            const newSummary = buildNewSummaryFromCard(loadedCardSurvey);
            const comparison = compareOldNewSummaries(oldSummary, newSummary);
            const normalizedCardSurvey: CardSurvey = {
              ...loadedCardSurvey,
              paths: (loadedCardSurvey.paths || []).map((p) => ({
                ...p,
                trigger: {
                  ...p.trigger,
                  values: normalizeTriggerValues(p.trigger?.values),
                },
              })),
            };
            setMigrationState({
              oldPayload: normalizedSurvey.questions,
              convertedCardSurvey: normalizedCardSurvey,
              comparison,
            });
            skipSettingCardSurvey = true;
            safeNodes = rawNodes.map((node) => normalizeNodeToNewFormat(node as LegacyNode));
            safeEdges =
              normalizedSurvey.questions.edges && Array.isArray(normalizedSurvey.questions.edges)
                ? (normalizedSurvey.questions.edges as LegacyEdge[]).map((edge: LegacyEdge) => normalizeEdgeToNewFormat(edge, safeNodes))
                : [];
            safeEdges = removeDuplicateEdges(safeEdges);
          } else if (Array.isArray(normalizedSurvey.questions) && (normalizedSurvey.questions as unknown[]).length > 0) {
            const flowData = generateFlowFromTemplate(normalizedSurvey.questions as Parameters<typeof generateFlowFromTemplate>[0]);
            const nodesWithMeta = ensureStartEndNodes(flowData.nodes);
            const edgesForCard = removeDuplicateEdges(flowData.edges);
            loadedCardSurvey = flowToCard(nodesWithMeta, edgesForCard);
            const oldSummary = buildOldSummaryFromFlow(nodesWithMeta, edgesForCard);
            const newSummary = buildNewSummaryFromCard(loadedCardSurvey);
            safeNodes = ensureStartEndNodes(flowData.nodes.map((node) => normalizeNodeToNewFormat(node as LegacyNode)));
            safeEdges = flowData.edges.map((edge) => normalizeEdgeToNewFormat(edge as LegacyEdge, safeNodes));
            safeEdges = removeDuplicateEdges(safeEdges);
            const comparison = compareOldNewSummaries(oldSummary, newSummary);
            const normalizedCardSurvey: CardSurvey = {
              ...loadedCardSurvey,
              paths: (loadedCardSurvey.paths || []).map((p) => ({
                ...p,
                trigger: {
                  ...p.trigger,
                  values: normalizeTriggerValues(p.trigger?.values),
                },
              })),
            };
            setMigrationState({
              oldPayload: normalizedSurvey.questions,
              convertedCardSurvey: normalizedCardSurvey,
              comparison,
            });
            skipSettingCardSurvey = true;
          }
        }

        if (loadedCardSurvey && !skipSettingCardSurvey) {
          const normalizedCardSurvey: CardSurvey = {
            ...loadedCardSurvey,
            paths: (loadedCardSurvey.paths || []).map((p) => ({
              ...p,
              trigger: {
                ...p.trigger,
                values: normalizeTriggerValues(p.trigger?.values),
              },
            })),
          };
          handleCardSurveyChange(normalizedCardSurvey);
        } else if (!skipSettingCardSurvey) {
          handleCardSurveyChange({
            format: 'card-v1',
            questions: [],
            paths: [],
          });
        }
      } catch (err: unknown) {
        console.error('Failed to fetch survey:', err instanceof Error ? err.message : err);
        toast.error('Failed to load survey');
        navigate('/surveys');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSurvey();
  }, [id, isEditing, navigate, removeDuplicateEdges, handleCardSurveyChange]);
  
  useEffect(() => {
    if (!isEditing) {
      if (!cardSurvey) {
        handleCardSurveyChange({
          format: 'card-v1',
          questions: [],
          paths: [],
        });
      }
      
      const fetchSurveys = async () => {
        try {
          const response = await api.get('/surveys');
          const surveys = getSurveysListFromResponse(response?.data) as Survey[];
          const ids = surveys.map((s: Survey) => Number((s as { id?: unknown }).id)).filter(Number.isFinite);
          const nextId = ids.length > 0 ? Math.max(...ids) + 1 : 1;
          const expectedLink = buildSurveyLink(nextId, surveyDataReward);
          setSurveyLink(expectedLink);
        } catch (err) {
          console.error('Failed to fetch surveys for next ID:', err);
          setSurveyLink(buildSurveyLink(1, surveyDataReward));
        }
      };
      fetchSurveys();
    }
  }, [isEditing, cardSurvey, surveyDataReward, handleCardSurveyChange]);

  useEffect(() => {
    if (editingSurvey) {
      setSurveyLink(buildSurveyLink(editingSurvey.id, surveyDataReward));
    }
  }, [editingSurvey, surveyDataReward]);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setSurveyStartDate(newStartDate);
    
    if (validationErrors.startDate) {
      setValidationErrors(prev => ({ ...prev, startDate: undefined }));
    }
    
    if (surveyEndDate && newStartDate) {
      const start = new Date(newStartDate);
      const end = new Date(surveyEndDate);
      if (end < start) {
        setValidationErrors(prev => ({ ...prev, endDate: 'End date must be after start date' }));
      } else if (validationErrors.endDate) {
        setValidationErrors(prev => ({ ...prev, endDate: undefined }));
      }
    }
  };
  
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    setSurveyEndDate(newEndDate);
    
    if (validationErrors.endDate) {
      setValidationErrors(prev => ({ ...prev, endDate: undefined }));
    }
    
    if (surveyStartDate && newEndDate) {
      const start = new Date(surveyStartDate);
      const end = new Date(newEndDate);
      if (end < start) {
        setValidationErrors(prev => ({ ...prev, endDate: 'End date must be after start date' }));
      }
    }
  };
  
  
  const surveyValidationErrors = useMemo(
    () => (cardSurvey ? getErrors(validateSurvey(cardSurvey)) : []),
    [cardSurvey]
  );
  const hasSurveyErrors = surveyValidationErrors.length > 0;

  const handleSave = useCallback(async () => {
    setSaving(true);
    
    try {
      const surveyToSave = cardSurveyRef.current ?? cardSurvey;

      if (!surveyToSave || countRealQuestions(surveyToSave) === 0) {
        toast.error('Please create at least one question');
        setSaving(false);
        return;
      }

      const saveValidationErrors = getErrors(validateSurvey(surveyToSave));
      if (saveValidationErrors.length > 0) {
        const messages = saveValidationErrors.slice(0, 3).map((e) => e.message);
        toast.error(
          `Survey has ${saveValidationErrors.length} error(s). Fix them before saving. ${messages.join(' ')}`
        );
        setSaving(false);
        return;
      }
      
      const flowData = cardToFlow(surveyToSave);
      const surveyArray = convertFlowToSurveyFormat(flowData.nodes, flowData.edges);
      
      const surveyData: SurveySavePayload = {
        name: surveyName.trim(),
        description: surveyDescription.trim(),
        is_active: Boolean(surveyIsActive),
      };
      
      const dataRewardStr = (surveyDataReward != null ? String(surveyDataReward) : '').trim();
      const n = dataRewardStr !== '' ? parseFloat(dataRewardStr) : NaN;
      surveyData.data_reward =
        dataRewardStr === '' || Number.isNaN(n) ? '0' : n < 0 ? '0' : dataRewardStr;
      
      if (surveyBrandId !== null && surveyBrandId !== undefined && surveyBrandId !== '') {
        surveyData.brand_id = surveyBrandId.trim();
      }
      
      if (surveyIconUrl !== null && surveyIconUrl !== undefined && surveyIconUrl.trim() !== '') {
        surveyData.icon_url = surveyIconUrl.trim();
      }
      
      const errors: { startDate?: string; endDate?: string } = {};
      const todayLocal = toDateInputValue(new Date());
      const existingStartOnly = editingSurvey?.start_date
        ? toDateInputValue(editingSurvey.start_date)
        : '';
      const existingEndOnly = editingSurvey?.end_date
        ? toDateInputValue(editingSurvey.end_date)
        : '';

      if (surveyStartDate && surveyStartDate.trim()) {
        const startDateOnly = surveyStartDate.trim();
        if (startDateOnly < todayLocal) {
          if (!isEditing || startDateOnly !== existingStartOnly) {
            errors.startDate = 'Start date cannot be before today';
          }
        }
        surveyData.start_date = new Date(`${startDateOnly}T12:00:00`).toISOString();
      } else {
        errors.startDate = 'Start date is required';
      }

      if (surveyEndDate && surveyEndDate.trim()) {
        const endDateOnly = surveyEndDate.trim();
        if (endDateOnly < todayLocal) {
          if (!isEditing || endDateOnly !== existingEndOnly) {
            errors.endDate = 'End date cannot be before today';
          }
        }
        if (surveyStartDate && endDateOnly < surveyStartDate.trim()) {
          errors.endDate = 'End date must be after start date';
        }
        surveyData.end_date = new Date(`${endDateOnly}T12:00:00`).toISOString();
      } else {
        errors.endDate = 'End date is required';
      }
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        const errorMessages = Object.values(errors).filter(Boolean);
        toast.error(errorMessages.join(', '));
        setSaving(false);
        return;
      }
      
      setValidationErrors({});
      
      if (surveyLink && surveyLink.trim()) {
        surveyData.link = surveyLink.trim();
      } else if (!isEditing) {
        toast.error('Link is required');
        setSaving(false);
        return;
      }
      
      surveyData.questions = {
        ...surveyToSave,
        flowFormat: {
          nodes: flowData.nodes,
          edges: flowData.edges,
          survey: surveyArray,
        },
      };
      
      if (isEditing && editingSurvey) {
        await api.put(`/surveys/${editingSurvey.id}`, surveyData);
        toast.success('Survey updated successfully');
      } else {
        const createResponse = await api.post('/surveys', surveyData);
        const newSurveyId = createResponse.data?.id;
        if (newSurveyId == null) {
          toast.error('Survey created but no ID returned');
          setSaving(false);
          return;
        }
        const actualLink = buildSurveyLink(newSurveyId, surveyDataReward);
        if (surveyData.link !== actualLink) {
          await api.put(`/surveys/${newSurveyId}`, { link: actualLink });
        }
        
        toast.success('Survey created successfully');
      }
      
      navigate('/surveys');
    } catch (err: unknown) {
      const errObj = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: unknown; error?: string } } }).response
        : undefined;
      const data = errObj?.data;
      let errorMessage = 'Failed to save survey';
      if (data) {
        const msg = data.message;
        const errStr = data.error;
        if (Array.isArray(msg)) errorMessage = msg.join(', ');
        else if (typeof msg === 'string') errorMessage = msg;
        else if (typeof errStr === 'string') errorMessage = errStr;
      }
      if (/before today/i.test(errorMessage)) {
        errorMessage += ' If your dates are correct, ensure your device date and time are set correctly.';
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [
    cardSurvey,
    surveyName,
    surveyDescription,
    surveyLink,
    surveyStartDate,
    surveyEndDate,
    surveyDataReward,
    surveyBrandId,
    surveyIconUrl,
    surveyIsActive,
    isEditing,
    editingSurvey,
    navigate,
  ]);
  
  const handleConfirmMigration = useCallback(() => {
    if (!migrationState) return;
    handleCardSurveyChange(migrationState.convertedCardSurvey);
    setMigrationState(null);
  }, [migrationState, handleCardSurveyChange]);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl py-4">
        <div className="flex min-h-[400px] items-center justify-center">
          <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (migrationState) {
    return (
      <div className="mx-auto max-w-7xl py-4">
        <div className="mb-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate('/surveys')}
            aria-label="Back to surveys"
          >
            <IconArrowLeft className="size-4" />
          </Button>
          <h2 className="text-xl font-semibold">Edit Survey — Format Migration</h2>
        </div>
        <MigrationComparisonView
          oldPayload={migrationState.oldPayload}
          convertedCardSurvey={migrationState.convertedCardSurvey}
          comparison={migrationState.comparison}
          onConfirmUseNewFormat={handleConfirmMigration}
          onCancel={() => navigate('/surveys')}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl py-4">
      <Card className="rounded-lg border border-border/60 p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => navigate('/surveys')}
            >
              <IconArrowLeft className="size-4" />
            </Button>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Survey' : 'Create New Survey'}
            </h2>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/surveys')} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={
                saving ||
                !surveyName.trim() ||
                !surveyDescription.trim() ||
                !surveyLink.trim() ||
                !surveyStartDate ||
                !surveyEndDate ||
                !isDateRangeValid ||
                !isBrandIdValid ||
                !hasAtLeastOneQuestion
              }
            >
              {saving ? <IconLoader2 className="size-4 animate-spin" /> : <IconDeviceFloppy className="size-4" />}
              {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>

        {hasSurveyErrors && (
          <SurveyValidationAlert issues={surveyValidationErrors} />
        )}
        
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
          <SurveyFormFields
            surveyName={surveyName}
            surveyDescription={surveyDescription}
            surveyLink={surveyLink}
            surveyStartDate={surveyStartDate}
            surveyEndDate={surveyEndDate}
            surveyDataReward={surveyDataReward}
            surveyBrandId={surveyBrandId}
            surveyIconUrl={surveyIconUrl}
            surveyIsActive={surveyIsActive}
            brandOptions={brandOptions}
            brandOptionLabels={brandOptionLabels}
            validationErrors={validationErrors}
            minStartDate={
              (() => {
                const today = new Date().toISOString().split('T')[0];
                if (isEditing && surveyStartDate && surveyStartDate < today) return surveyStartDate;
                return today;
              })()
            }
            minEndDate={
              (() => {
                const today = new Date().toISOString().split('T')[0];
                if (isEditing && surveyEndDate && surveyEndDate < today) return surveyEndDate;
                return today;
              })()
            }
            editingSurvey={editingSurvey}
            expandedAccordion={expandedAccordion[0] || false}
            onAccordionChange={(val) => setExpandedAccordion(val ? [val as string] : [])}
            onSurveyNameChange={setSurveyName}
            onSurveyDescriptionChange={setSurveyDescription}
            onSurveyLinkChange={setSurveyLink}
            onSurveyStartDateChange={handleStartDateChange}
            onSurveyEndDateChange={handleEndDateChange}
            onSurveyDataRewardChange={setSurveyDataReward}
            onSurveyBrandIdChange={setSurveyBrandId}
            onSurveyIconUrlChange={setSurveyIconUrl}
            onSurveyIsActiveChange={setSurveyIsActive}
          />

          <Accordion
            value={expandedAccordion}
            onValueChange={setExpandedAccordion}
          >
            <AccordionItem value="panel2" className="mb-3 rounded-lg border shadow-sm">
              <AccordionTrigger className="px-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <IconTool className="size-5 text-primary" />
                  <span className="text-base font-semibold">Survey Builder</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-0 pb-3">
                <CardSurveyBuilder
                  initialSurvey={cardSurvey || { format: 'card-v1', questions: [], paths: [] }}
                  onSurveyChange={handleCardSurveyChange}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="panel3" className="mb-3 rounded-lg border shadow-sm">
              <AccordionTrigger className="px-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <IconEye className="size-5 text-primary" />
                  <span className="text-base font-semibold">Survey Preview</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <SurveyPreview survey={cardSurvey || null} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </Card>
    </div>
  );
}
